import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import type {Value} from '@netx/core-asset/models/attribute';
import {valuesMatch} from '@netx/core-components/Form/utils';
import {useRefState, useUpdateEffect} from '@netx/core-components/hooks';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {i18n} from '@netx/core/i18n';
import {isEmptyValue} from '@netx/core/utils';
import {ATTRIBUTE_APPENDABLE_TYPES} from '../../constants';
import type {AttributeAction} from '../../types';
import {actionChangesValue} from '../../utils';
import validators from '../../validators';
import {
	EditableAttribute,
	type EditableAttributeProps,
} from './EditableAttribute';

export interface LegacyFormEditableAttributeProps
	extends EditableAttributeProps {
	form: any;
	mode?: AttributeAction;
	originalValue?: any;
}

const hasChanged = (
	value: Value,
	originalValue: Value,
	multipleValues: boolean,
	action: AttributeAction,
) =>
	!valuesMatch(value, originalValue, multipleValues) ||
	actionChangesValue(value, originalValue, action);

/**
 * `react-hook-form` wrapper for `EditableAttribute`
 */
export const LegacyFormEditableAttributePrimitive = (
	{form, mode, ...props}: LegacyFormEditableAttributeProps,
	ref: any,
) => {
	const {
		appendable,
		attrData,
		clearable,
		forceClearable,
		enforceMandatory,
		action,
		multipleValues,
		value,
		originalValue,
	} = props;

	const fieldName = props.name || `${attrData.attributeid}`;

	const lastActionRef = useRef(action);
	const lastValueRef = useRef(value);

	const checkRequired = () => {
		// We are enforcing mandatory if we:
		// started with no value
		// do not have multiple values
		// If neither of those are true we fallback to the prop passed in
		return (
			attrData.mandatory === 1 &&
			(!isEmptyValue(originalValue) && !multipleValues
				? true
				: enforceMandatory)
		);
	};

	const canAppend =
		appendable && ATTRIBUTE_APPENDABLE_TYPES.includes(attrData.type);
	const [required, requiredRef, setRequired] = useRefState(checkRequired());

	const modeRef = useRef(mode);
	useEffect(() => {
		modeRef.current = mode;
	}, [mode]);

	const getDisabledActions = () => {
		const disabledActions: AttributeAction[] = [];
		if (!clearable || (!forceClearable && attrData.mandatory === 1)) {
			disabledActions.push('clear');
		}
		return disabledActions;
	};

	const [disabledActions, setDisabledActions] = useState<AttributeAction[]>(
		getDisabledActions(),
	);

	/**
	 * Set attribute value
	 * A helper so you do not need to keep proving `attrData.name`
	 */
	const setValue = useConstCallback((value: Value) =>
		form?.setValue(fieldName, value),
	);

	/**
	 * Set attribute action
	 * A helper so you do not need to keep proving `attrData.name`
	 */
	const setAction = useConstCallback((newAction: AttributeAction) => {
		if (newAction === 'clear') {
			// If the new `action` is `clear`, clear the value
			setValue(null);
		} else if (action === 'clear') {
			// If the current `action` is `clear`, and the value is null,
			// set the value back to original
			value == null && setValue(originalValue);
		}
		form?.setAction(fieldName, newAction);
	});

	// On every render we will check the value
	// We want to know if the value impacts the action
	useLayoutEffect(() => {
		// Do nothing if there was no change
		if (lastValueRef.current === value) return;

		lastValueRef.current = value;

		const cleared = action === 'clear';
		let newAction = action;
		if (cleared && value != null) {
			newAction = mode || 'overwrite';
		} else if (
			value == null &&
			!disabledActions.includes('clear') &&
			!isEmptyValue(originalValue) &&
			!multipleValues
		) {
			newAction = 'clear';
		}

		newAction !== action && setAction(newAction as any);
	});

	// On every render we will check the action
	// We want to know if the action impacts the value
	// or was an invalid action
	useLayoutEffect(() => {
		// Do nothing if there was no change
		if (lastActionRef.current === action) return;

		const lastAction = lastActionRef.current;

		lastActionRef.current = action;
		// We were passed down an action that is not allowed
		if (disabledActions.includes(action as any)) {
			setAction(mode !== action ? mode || 'overwrite' : 'overwrite');
			return;
		}

		let newValue = value;
		// Put back original value when last action was `clear`
		if (lastAction === 'clear' && value === null) newValue = originalValue;
		// Empty value on action `clear`
		if (action === 'clear') newValue = null;

		setValue(newValue);
	});

	// Setup and tear down of our component
	// This will cause an initial re-render due to the action
	useEffect(() => {
		const validator = (value: Value) =>
			requiredRef.current && isEmptyValue(value)
				? new Error(i18n.t('i18n.mandatory'))
				: validators[attrData.type as keyof typeof validators](value, attrData);

		form?.register(fieldName, {
			validator,
			hasChanged,
			value,
			action: modeRef.current,
		});

		return () => {
			form?.unregister(fieldName);
		};
	}, [fieldName, attrData.type]);

	// Props related to the fields requirement have changed
	useUpdateEffect(() => {
		setRequired(checkRequired());
	}, [attrData.mandatory, enforceMandatory, originalValue]);

	// Props related to the fields validation have changed
	useUpdateEffect(() => {
		// Only need to re-run validation for fields with an error already
		// if the field was not valid, it will be caught at submit
		form?.errorsRef.current[fieldName] && form?.validateField(fieldName, value);
	}, [attrData.mandatory, enforceMandatory]);

	// Props related to disabled/enabled actions have changed
	useUpdateEffect(() => {
		setDisabledActions(getDisabledActions());
	}, [clearable, forceClearable, attrData.mandatory]);

	return (
		<EditableAttribute
			ref={ref}
			{...props}
			action={action}
			value={value}
			onActionChange={splitEventHandler(props.onActionChange, setAction)}
			onValueChange={(value, action) => {
				setValue(value);
			}}
		/>
	);
};

export const LegacyFormEditableAttribute = React.forwardRef(
	LegacyFormEditableAttributePrimitive,
);

export default LegacyFormEditableAttribute;
